import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";
import { API_ENDPOINTS, ROUTES } from "../config/constants";
import { Link } from "react-router-dom";
import {
    Building2,
    Loader2,
    Lock,
    Mail,
    Sparkles,
    User,
    UserPlus,
} from "lucide-react";
import PasswordInput from "../components/PasswordInput";

const validate = (email, password, companyName, fullName) => {
    const errors = {};
    if (!fullName.trim()) errors.fullName = "Full name is required.";
    if (!companyName.trim()) errors.companyName = "company name is required";
    if (!email.trim()) errors.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
        errors.email = "Invalid email address.";
    if (!password) errors.password = "Password is required.";
    else if (password.length < 6)
        errors.password = "Password must be at least 6 characters.";
    return errors;
};

function Signup() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [companyName, setCompantName] = useState("");
    const [fullName, setFullName] = useState("");
    const [fieldErrors, setFieldErrors] = useState({});
    const [error, setError] = useState("");
    const [isSigningUp, setIsSigningUp] = useState(false);

    const navigate = useNavigate();

    const handleSignup = async (e) => {
        e.preventDefault();
        if (isSigningUp) return;

        setError("");

        const errors = validate(email, password, companyName, fullName);
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }

        const trimmedFullName = fullName.trim();
        const trimmedCompanyName = companyName.trim();

        setIsSigningUp(true);

        try {
            const response = await api.post(API_ENDPOINTS.SIGNUP, {
                email: email.trim(),
                password,
                company_name: trimmedCompanyName,
                full_name: trimmedFullName,
            });

            if (!response?.data) {
                setError("Error in creating account");
                return;
            }
            navigate(ROUTES.VERIFY_EMAIL, {
                state: {
                    email: response.data.email || email,
                },
            });
        } catch (error) {
            if (error.response?.status === 422) {
                setError("Please fill all required fields");
            } else if (error.response?.status === 400) {
                setError("User already exist");
            }
            console.error(error);
        } finally {
            setIsSigningUp(false);
        }
    };

    return (
        <div className="flex min-h-[calc(100vh-64px)] items-center justify-center bg-[#F8F8FE] px-3 py-8 sm:px-6 lg:px-8">
            <div className="grid w-full max-w-5xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm lg:grid-cols-[0.95fr_1.05fr]">
                <div className="hidden bg-gradient-to-br from-indigo-600 via-indigo-500 to-violet-500 p-8 text-white lg:flex lg:flex-col lg:justify-between">
                    <div>
                        <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/20">
                            <Sparkles size={24} aria-hidden="true" />
                        </div>
                        <h1 className="mt-8 text-3xl font-bold leading-tight">
                            Build your outbound workspace
                        </h1>
                        <p className="mt-4 max-w-sm text-sm leading-6 text-indigo-50">
                            Set up your profile once, then search leads, Create products and outreach campaigns.
                        </p>
                    </div>
                    <p className="text-xs font-medium uppercase tracking-[0.2em] text-indigo-100">
                        OptiMind account setup
                    </p>
                </div>

                <div className="w-full p-5 sm:p-8 lg:p-10">
                    <div className="mx-auto max-w-md">
                        <div className="mb-6 text-center sm:mb-8">
                            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 lg:hidden">
                                <Sparkles size={24} aria-hidden="true" />
                            </div>
                            <p className="text-sm font-medium text-indigo-600">
                                Start your AI outreach workspace
                            </p>
                            <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">
                                Create Account
                            </h2>
                        </div>

                        {error && (
                            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-center text-sm font-medium text-red-600">
                                {error}
                            </div>
                        )}

                        <form
                            onSubmit={handleSignup}
                            noValidate
                            className="space-y-4"
                        >
                            <div>
                                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                                    Full Name
                                </label>

                                <div className="relative">
                                    <User
                                        size={18}
                                        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                                        aria-hidden="true"
                                    />
                                    <input
                                        type="text"
                                        placeholder="Full name"
                                        value={fullName}
                                        onChange={(e) => {
                                            setFullName(e.target.value);
                                            if (fieldErrors.fullName)
                                                setFieldErrors((prev) => ({
                                                    ...prev,
                                                    fullName: undefined,
                                                }));
                                        }}
                                        className={`w-full rounded-xl border bg-white px-10 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 ${
                                            fieldErrors.fullName
                                                ? "border-red-400"
                                                : "border-slate-200"
                                        }`}
                                    />
                                </div>
                                {fieldErrors.fullName && (
                                    <p className="mt-1.5 text-xs text-red-600">
                                        {fieldErrors.fullName}
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                                    Company Name
                                </label>

                                <div className="relative">
                                    <Building2
                                        size={18}
                                        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                                        aria-hidden="true"
                                    />
                                    <input
                                        type="text"
                                        placeholder="Company name"
                                        value={companyName}
                                        onChange={(e) => {
                                            setCompantName(e.target.value);
                                            if (fieldErrors.companyName)
                                                setFieldErrors((prev) => ({
                                                    ...prev,
                                                    companyName: undefined,
                                                }));
                                        }}
                                        className={`w-full rounded-xl border bg-white px-10 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 ${
                                            fieldErrors.companyName
                                                ? "border-red-400"
                                                : "border-slate-200"
                                        }`}
                                    />
                                </div>
                                {fieldErrors.companyName && (
                                    <p className="mt-1.5 text-xs text-red-600">
                                        {fieldErrors.companyName}
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                                    Email ID
                                </label>
                                <div className="relative">
                                    <Mail
                                        size={18}
                                        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                                        aria-hidden="true"
                                    />
                                    <input
                                        type="email"
                                        placeholder="you@example.com"
                                        value={email}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            if (fieldErrors.email)
                                                setFieldErrors((prev) => ({
                                                    ...prev,
                                                    email: undefined,
                                                }));
                                        }}
                                        className={`w-full rounded-xl border bg-white px-10 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 ${
                                            fieldErrors.email
                                                ? "border-red-400"
                                                : "border-slate-200"
                                        }`}
                                    />
                                </div>
                                {fieldErrors.email && (
                                    <p className="mt-1.5 text-xs text-red-600">
                                        {fieldErrors.email}
                                    </p>
                                )}
                            </div>

                            <div>
                                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                                    Password
                                </label>
                                <PasswordInput
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        if (fieldErrors.password)
                                            setFieldErrors((prev) => ({
                                                ...prev,
                                                password: undefined,
                                            }));
                                    }}
                                    hasError={Boolean(fieldErrors.password)}
                                    leadingIcon={Lock}
                                />
                                {fieldErrors.password && (
                                    <p className="mt-1.5 text-xs text-red-600">
                                        {fieldErrors.password}
                                    </p>
                                )}
                            </div>

                            <button
                                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-400"
                                type="submit"
                                disabled={isSigningUp}
                            >
                                {isSigningUp ? (
                                    <Loader2
                                        size={18}
                                        className="animate-spin"
                                        aria-hidden="true"
                                    />
                                ) : (
                                    <UserPlus size={18} aria-hidden="true" />
                                )}
                                {isSigningUp ? "Creating account..." : "Sign Up"}
                            </button>
                            <p className="text-center text-sm text-slate-600">
                                Already have an account?{" "}
                                <Link
                                    to="/login"
                                    className="font-semibold text-indigo-600 transition hover:text-indigo-700"
                                >
                                    Login
                                </Link>
                            </p>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Signup;
